import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Login from '../screens/login';
import LoadingScreen from './LoadingScreen';
import { ThemeProvider, createGlobalStyle } from 'styled-components';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  body {
    background: ${({ theme }) => theme.body};
    color: ${({ theme }) => theme.text};
    font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    -webkit-font-smoothing: antialiased;
  }
`;

const darkTheme = {
  bar: "rgba(13, 15, 20, 0.95)",
  primary: "#10b981",
  primaryDark: "#059669",
  primaryLight: "#34d399",
  secondary: "rgba(255, 255, 255, 0.05)",
  tertiary: "#4835efff",
  header: "#0d0f14",
  body: "#0d0f14",
  text: "#e5e7eb",
  textSecondary: "rgba(255, 255, 255, 0.45)",
  border: "rgba(255, 255, 255, 0.06)",
  border2: "rgba(255, 255, 255, 0.08)",
  border3: "rgba(255, 255, 255, 0.03)",
  card: "rgba(18, 20, 26, 0.95)",
  card2: "rgba(24, 27, 34, 0.9)",
  cardBg: "rgba(18, 20, 26, 0.98)",
  cardBg2: "rgba(24, 27, 34, 0.95)",
  tableRow: "rgba(18, 21, 26, 0.9)",
  tableRowHover: "rgba(30, 33, 42, 1)",
  buttonBg: "rgba(24, 27, 34, 0.95)",
  buttonHover: "rgba(36, 40, 50, 0.5)",
  contrast: "rgba(255, 255, 255, 0.02)",
  contrast2: "rgba(255, 255, 255, 0.04)",
  load: "rgba(0, 0, 0, .3)",
  load2: "rgba(0, 0, 0, .5)",
  radius: "12px",
  radiusSm: "8px",
  radiusLg: "16px",
  success: "#10b981",
  error: "#ef4444",
  warning: "#f59e0b",
  info: "#3b82f6",
};

const ProtectedRoute = ({ children, requiredEmail }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Salvar a URL atual para redirecionar após o login
    if (!isAuthenticated && !loading) {
      sessionStorage.setItem('redirectAfterLogin', location.pathname);
    }
  }, [isAuthenticated, loading, location.pathname]);

  useEffect(() => {
    // Após autenticação bem-sucedida, verificar se há URL para redirecionar
    if (isAuthenticated && !loading) {
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (redirectPath && redirectPath !== location.pathname) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath, { replace: true });
      }
    }
  }, [isAuthenticated, loading, location.pathname, navigate]);

  if (loading) {
    return (
      <ThemeProvider theme={darkTheme}>
        <GlobalStyle />
        <LoadingScreen isDark={true} />
      </ThemeProvider>
    );
  }

  // Se não estiver autenticado, mostrar tela de login
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={darkTheme}>
        <GlobalStyle />
        <Login />
      </ThemeProvider>
    );
  }

  // Se está autenticado mas não tem o email correto
  if (requiredEmail && user?.email !== requiredEmail) {
    return children; // O próprio componente filho vai mostrar a tela de acesso negado
  }

  return children;
};

export default ProtectedRoute;
